import React from 'react';

import { RouteComponentProps } from '@reach/router';
import { Formik } from 'formik';

import Divider from '@components/Divider';
import CandidateProgress from '@components/Formik/CandidateProgress';
import LinkPrevious from '@components/LinkPrevious';

import CandidateList from './CandidateList';
import CandidateProgressTable from './CandidateProgressTable';

import { getFormInformation } from '@domain/leads/join-form/formInfo';
import {
  getUpdateProgressClubOptions,
  getUpdateProgressDistrictOptions,
} from '@domain/leads/join-form/optionsList';

import {
  getCovertCandidateToMemberPath,
  getDistrictMembershipCandidatePath,
  getIdentifyClubCandidatePath,
  getMembershipCandidatePath,
} from '@use-cases/leads';

import { localizedNavigate } from '@utils/localized-navigate';

import { useTranslation } from '@external/react-i18next';

interface Props extends RouteComponentProps {
  clubId: string;
  districtId: string;
  riDistrictId?: string;
  isDistrict: boolean;
}
interface progressValues {
  progress?: string;
  candidateCheck?: string;
  additionalNotes?: string;
  confirmAsMember?: boolean;
  clubName?: string;
}

const CandidateDetails: React.FC<Props> = ({
  clubId,
  districtId,
  riDistrictId,
  isDistrict,
}) => {
  const { t } = useTranslation();

  const fieldProps = getFormInformation(t, 'candidateDetails');

  const customSelectOptionsForClub = getUpdateProgressClubOptions(t);

  const customSelectOptionsForDistrict = getUpdateProgressDistrictOptions(t);

  const handleSubmit = (values: progressValues) => {
    if (values.confirmAsMember) {
      if (!isDistrict) {
        if (values.candidateCheck === 'clubCandidate') {
          localizedNavigate(getIdentifyClubCandidatePath(clubId));
        } else {
          localizedNavigate(
            getCovertCandidateToMemberPath(clubId, 'individualId')
          );
        }
      } else if (values.clubName === '') {
        localizedNavigate(
          `/district/${districtId}/manage-membership/convert-candidate`
        );
      } else {
        localizedNavigate(
          `/district/${districtId}/manage-membership/identify-candidate/${values.clubName}`
        );
      }
    }
  };

  function handleBackClick() {
    if (!isDistrict) return getMembershipCandidatePath(clubId);

    return getDistrictMembershipCandidatePath(districtId);
  }

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      {({ values }) => {
        return (
          <>
            <div className="mt-5">
              <LinkPrevious
                path={handleBackClick()}
                label={t('mml.add-candidate-details.back-link', 'Back')}
              />
            </div>
            <div className="flex flex-col mt-5 desktop:flex-row">
              <div className="desktop:w-3/5 desktop:mr-15 w-full">
                <div className="flex justify-between">
                  <h2 className="desktop:mb-0">
                    {t('mml.add-candidate-details.title', 'Candidate details')}
                  </h2>
                </div>
                <div className="flex flex-col">
                  <div className="my-4">
                    <h3 className="font-normal text-lg text-black desktop:mb-1">
                      {t('mml.add-candidate-details.name', 'Deepika Venkatesh')}
                    </h3>
                    <a href="deepika.venkatesh@gmail.com">
                      {t('deepika.venkatesh@gmail.com')}
                    </a>
                    <p className="m-0">{t('+91 123-456-7890')}</p>
                  </div>
                  <div className="my-4">
                    <h5>
                      {t('mml.add-candidate-details.address', 'Address (Home)')}
                    </h5>
                    <p className="m-0">E/1 2nd. Flr. </p>
                    <p className="m-0">Parsn Paradise Apts.</p>
                    <p className="m-0">1234 G.N. Chetty Rd.</p>
                    <p className="m-0">Madras,600017, India </p>
                    <p className="m-0">Tamil Nadu</p>
                  </div>
                  <div className="my-4">
                    <h5>{fieldProps.birthYear.label}</h5>
                    <span>2004</span>
                  </div>
                  <div className="my-4">
                    <h5>{fieldProps.gender.label}</h5>
                    <span>Female</span>
                  </div>
                  <div className="my-4">
                    <h5>{fieldProps.profession.label}</h5>
                    <span>Arts, Design, Entertainment, Sports, and Media</span>
                  </div>
                  <div className="my-4">
                    <h5>
                      {t(
                        'mml.add-candidate-details.reason',
                        'Reason to get involved'
                      )}
                    </h5>
                    <span>
                      {t(
                        'Ut sed neque bibendum, luctus dolor gravida, consequat arcu. Aenean in suscipit ligula. Fusce in ligula feugiat, pharetra elit sit amet, tempus dui.'
                      )}
                    </span>
                  </div>
                  <div className="my-4">
                    <h5>{fieldProps.memberId.label}</h5>
                    <span>11446101</span>
                  </div>
                </div>
              </div>
              <div className="desktop:w-2/5 w-full">
                <CandidateList />
              </div>
            </div>
            <div className="desktop:w-3/5">
              <Divider size={8} />
            </div>
            <div className="tablet:w-1/2 desktop:w-1/2 mb-12">
              <h2 className="desktop:mb-8">
                {t('mml.add-candidate-progress.title', 'Candidate progress')}
              </h2>
              {!isDistrict ? (
                <CandidateProgress
                  id={clubId}
                  isDistrict={isDistrict}
                  riDistrictId={riDistrictId}
                  onSubmitClick={() => handleSubmit(values)}
                  progressListOptions={customSelectOptionsForClub}
                />
              ) : (
                <CandidateProgress
                  id={districtId}
                  isDistrict={isDistrict}
                  riDistrictId={riDistrictId}
                  onSubmitClick={() => handleSubmit(values)}
                  progressListOptions={customSelectOptionsForDistrict}
                />
              )}
            </div>
            <div className="mt-5">
              <CandidateProgressTable />
            </div>
          </>
        );
      }}
    </Formik>
  );
};
export default CandidateDetails;
